import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useLineMachines } from "@/features/lines/hooks";
import { useParameters } from "@/features/line-editor/hooks";

interface TargetPickerProps {
  lineId: string;
  /** As máquinas CONGELADAS no ato de aplicar — o escopo em que um alvo faz sentido (FR-020). */
  committedMachineIds: readonly string[];
  machineId: string | null;
  parameterId: string | null;
  onChange: (target: { machineId: string | null; parameterId: string | null }) => void;
}

// Spec 077 (FR-020, FR-021) — O ALVO da premissa incorreta: máquina + parâmetro.
//
// Duas restrições, e nenhuma delas é cosmética:
//  · a MÁQUINA vem do escopo CONGELADO, não da linha de hoje. Não faz sentido apontar como premissa
//    errada um parâmetro de máquina que o compromisso nem abrangia — e a linha pode ter mudado
//    desde a vigência.
//  · o PARÂMETRO precisa alimentar a simulação (`influencesSimulation`). É o filtro que separa o
//    que o gêmeo usa do que só existe para status ou contagem de produção; uma premissa incorreta
//    é, por definição, sobre o primeiro grupo.
//
// ⚠️ O servidor REVALIDA as duas (V5). Este seletor existe para a pessoa não errar, não para
// autorizar: confiar na lista que a tela ofereceu é IDOR esperando acontecer.
//
// ⚠️ Os parâmetros vêm de `useParameters`, a chave CANÔNICA do recurso (`["parameters", machineId]`).
// Uma consulta própria com chave própria para a mesma rota não é duplicação inofensiva: TODAS as
// mutações de parâmetro invalidam a chave canônica, nenhuma alcançaria a outra, e o seletor
// continuaria oferecendo um parâmetro renomeado, excluído ou que deixou de alimentar a simulação —
// que o servidor então recusa com o 422 que esta tela acabou de dizer que não aconteceria.

export function TargetPicker({
  lineId,
  committedMachineIds,
  machineId,
  parameterId,
  onChange,
}: TargetPickerProps) {
  const machines = useLineMachines(lineId);
  const parameters = useParameters(machineId ?? "");

  const committed = (machines.data ?? []).filter((m) => committedMachineIds.includes(m.id));
  const influencing = (parameters.data ?? []).filter((p) => p.influencesSimulation);

  return (
    <div className="grid gap-3 rounded-md border border-border-subtle p-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="target-machine">Máquina</Label>
        <Select
          id="target-machine"
          value={machineId ?? ""}
          onChange={(e) => onChange({ machineId: e.target.value || null, parameterId: null })}
        >
          <option value="">Selecione…</option>
          {committed.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </Select>
        <p className="text-[11px] text-text-secondary">
          Só as máquinas que estavam no escopo comprometido desta janela.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="target-parameter">Parâmetro</Label>
        <Select
          id="target-parameter"
          value={parameterId ?? ""}
          disabled={!machineId}
          onChange={(e) => onChange({ machineId, parameterId: e.target.value || null })}
        >
          <option value="">{machineId ? "Selecione…" : "Escolha a máquina primeiro"}</option>
          {influencing.map((p) => (
            <option key={p.id} value={p.id}>
              {p.unit ? `${p.name} (${p.unit})` : p.name}
            </option>
          ))}
        </Select>
        <p className="text-[11px] text-text-secondary">
          {machineId && parameters.isSuccess && influencing.length === 0
            ? "Esta máquina não tem parâmetro que alimente a simulação — escolha outra máquina ou outra causa."
            : "Só os parâmetros que alimentam a simulação."}
        </p>
      </div>

      {/* ⚠️ Falha de leitura NÃO pode virar "nenhuma máquina comprometida" (FR-042): a primeira é
          "não sei", a segunda é uma afirmação sobre o compromisso — e ela empurra quem julga para
          outra causa. */}
      {machines.isError || parameters.isError ? (
        <p className="text-[12px] text-rd-danger-600 sm:col-span-2">
          Não foi possível carregar {machines.isError ? "as máquinas da linha" : "os parâmetros"}.
          Tente de novo antes de escolher o alvo — a lista pode estar incompleta.
        </p>
      ) : committed.length === 0 && machines.isSuccess ? (
        <p className="text-[12px] text-text-secondary sm:col-span-2">
          {/* Duas causas distintas para a mesma lista vazia, e dizer só a primeira mentia: uma
              máquina comprometida que foi APOSENTADA depois da vigência some de `useLineMachines`
              (que filtra `deletedAt`), enquanto `committedMachineIds` continua cheio — e o
              servidor ainda aceitaria aquele alvo. */}
          {committedMachineIds.length === 0
            ? "Este compromisso não registrou quais máquinas foram comprometidas — é um compromisso anterior ao congelamento de escopo, e não há como apontar um alvo dentro dele."
            : "Nenhuma das máquinas comprometidas nesta janela continua ativa na linha. Elas foram aposentadas depois da vigência — o escopo congelado segue registrado, mas não há o que selecionar aqui."}
        </p>
      ) : null}
    </div>
  );
}
